import type { Zone } from '@cocoonspace/sdk-js/types/zone'
import type { Offset } from 'maplibre-gl'
import {
	type ComponentProps,
	type ComponentPropsWithoutRef,
	type ComponentRef,
	forwardRef,
	useCallback,
} from 'react'
import { cn } from '@cocoonspace/ui/lib/utils'
import 'maplibre-gl/dist/maplibre-gl.css'
import _MapLibre, { Popup as _Popup, type MapRef } from 'react-map-gl/maplibre'

import { useMergeRefs } from '~/hooks/use-merge-refs'

export const MapLibre = forwardRef<
	MapRef,
	ComponentProps<typeof _MapLibre> & { zone?: Zone }
>(({ zone, ...props }, ref) => {
	const loadImages = useCallback(
		(mapRef: MapRef | null) => {
			if (!mapRef || zone !== 'paris') {
				return
			}
			if (mapRef.hasImage('subway')) {
				return
			}
			mapRef
				?.loadImage('/images/v2/stations/paris/metro/metro.png')
				.then((img) => {
					mapRef.addImage('subway', img.data)
				})
				.catch(() => {})
		},
		[zone],
	)

	const mergedRef = useMergeRefs([loadImages, ref])

	return (
		<_MapLibre
			{...props}
			attributionControl={false}
			dragRotate={false}
			mapStyle='/map-style.json'
			ref={mergedRef}
		/>
	)
})

MapLibre.displayName = 'Map'

export const Popup = forwardRef<
	ComponentRef<typeof _Popup>,
	ComponentPropsWithoutRef<typeof _Popup>
>(({ className, ...props }, ref) => {
	return (
		<_Popup
			{...props}
			className={cn(
				'[&>.maplibregl-popup-content]:!rounded-lg [&>.maplibregl-popup-content]:!bg-transparent [&>.maplibregl-popup-content]:!p-0 [&>.maplibregl-popup-content]:!font-sans',
				className,
			)}
			closeButton={false}
			maxWidth='300px'
			offset={[0, -20] as Offset}
			ref={ref}
		/>
	)
})

Popup.displayName = 'Popup'
